import React from "react";
import { Box, Card, Skeleton, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import HorizontalRuleIcon from "@mui/icons-material/HorizontalRule";

// Calcula a variação percentual. Trata os casos chatos: dividir por zero
// (previous=0) e ambos zero (sem variação real).
const computeDelta = (current, previous) => {
  const cur = Number(current) || 0;
  const prev = Number(previous) || 0;
  if (prev === 0 && cur === 0) return { pct: 0, kind: "flat" };
  if (prev === 0) return { pct: 100, kind: "up" };
  const diff = ((cur - prev) / Math.abs(prev)) * 100;
  if (Math.abs(diff) < 0.1) return { pct: 0, kind: "flat" };
  return { pct: +diff.toFixed(1), kind: diff > 0 ? "up" : "down" };
};

const KpiCard = ({
  title,
  value,
  previousValue,
  suffix,
  icon,
  color = "primary",
  // betterWhen: 'higher' (verde quando sobe) | 'lower' (verde quando cai)
  betterWhen = "higher",
  loading = false,
  hint,
}) => {
  const delta = computeDelta(value, previousValue);

  // Sobe = bom (verde) quando betterWhen='higher'; cai = bom (verde) quando
  // betterWhen='lower'. Ex: TMA caiu 10% é bom -> verde.
  const isGood =
    delta.kind === "flat"
      ? null
      : betterWhen === "higher"
      ? delta.kind === "up"
      : delta.kind === "down";

  const deltaColor =
    delta.kind === "flat"
      ? "text.secondary"
      : isGood
      ? "success.main"
      : "error.main";

  const ArrowIcon =
    delta.kind === "up"
      ? ArrowUpwardIcon
      : delta.kind === "down"
      ? ArrowDownwardIcon
      : HorizontalRuleIcon;

  return (
    <Card
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2,
        border: 1,
        borderColor: "divider",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 1,
        transition: "border-color 160ms ease",
        "&:hover": { borderColor: (theme) => alpha(theme.palette[color].main, 0.4) },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {icon && (
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: (theme) => alpha(theme.palette[color].main, 0.1),
              color: (theme) => theme.palette[color].main,
            }}
          >
            {icon}
          </Box>
        )}
        <Tooltip title={hint || ""} placement="top" arrow>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: 0.6,
              color: "text.secondary",
              fontSize: 11,
              cursor: hint ? "help" : "default",
            }}
          >
            {title}
          </Typography>
        </Tooltip>
      </Box>

      {loading ? (
        <Skeleton variant="text" width={120} height={42} />
      ) : (
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, letterSpacing: -0.5, lineHeight: 1.1 }}
        >
          {value}
          {suffix && (
            <Typography component="span" variant="body2" sx={{ ml: 0.5, color: "text.secondary", fontWeight: 500 }}>
              {suffix}
            </Typography>
          )}
        </Typography>
      )}

      {!loading && previousValue === undefined && (
        // Modo snapshot — sem comparativo. Mostra rótulo "ao vivo" pra deixar
        // claro que o número não responde ao filtro de período.
        <Typography
          variant="caption"
          sx={{
            mt: "auto",
            color: "text.secondary",
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <Box
            component="span"
            sx={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              bgcolor: "success.main",
              display: "inline-block",
            }}
          />
          ao vivo
        </Typography>
      )}

      {!loading && previousValue !== undefined && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: "auto" }}>
          <ArrowIcon sx={{ fontSize: 14, color: deltaColor }} />
          <Typography variant="caption" sx={{ color: deltaColor, fontWeight: 600 }}>
            {delta.pct === 0 ? "0%" : `${Math.abs(delta.pct)}%`}
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary", ml: 0.5 }}>
            vs período anterior
          </Typography>
        </Box>
      )}
    </Card>
  );
};

export default React.memo(KpiCard);
